// Blind ABX listening test, hydrated into blog post via gatsby-browser.js
import React from 'react'
import { withPrefix } from 'gatsby'

const CLIPS = {
  original: withPrefix('/audio/original.flac'),
  fdk_lpf: withPrefix('/audio/256k_fdk_lpf.flac'),
  fdk_nolpf: withPrefix('/audio/256k_fdk_nolpf.flac')
}

const NAMES = {
  original: 'Original',
  fdk_lpf: 'With LPF',
  fdk_nolpf: 'Without LPF'
}

const ALL_QUESTIONS = [
  { question: 'Which has the low-pass filter applied?', answer: 'fdk_lpf' },
  { question: 'Which is the original (lossless)?', answer: 'original' },
  { question: 'Which was encoded without the low-pass filter?', answer: 'fdk_nolpf' },
  { question: 'Which has the low-pass filter applied?', answer: 'fdk_lpf' },
  { question: 'Which is the original (lossless)?', answer: 'original' },
  { question: 'Which was encoded without the low-pass filter?', answer: 'fdk_nolpf' },
  { question: 'Which has the low-pass filter applied?', answer: 'fdk_lpf' },
  { question: 'Which is the original (lossless)?', answer: 'original' },
  { question: 'Which was encoded without the low-pass filter?', answer: 'fdk_nolpf' },
  { question: 'Which has the low-pass filter applied?', answer: 'fdk_lpf' },
]

function shuffle(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const styles = {
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '2rem', maxWidth: '500px', width: '100%', margin: '0 auto', textAlign: 'center' },
  progress: { fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' },
  progressBar: { width: '100%', height: '4px', background: '#e0e0e0', borderRadius: '2px', marginBottom: '1.5rem', overflow: 'hidden' },
  progressFill: pct => ({ height: '100%', width: pct + '%', background: '#2c7bb6', borderRadius: '2px', transition: 'width 0.3s ease' }),
  question: { fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.4' },
  players: { display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1.5rem' },
  btn: { flex: 1, padding: '0.6rem 0.5rem', border: '1px solid #ddd', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  btnSelected: { border: '2px solid #2c7bb6', background: '#e8f4fd' },
  btnCorrect: { border: '2px solid #27ae60', background: '#eafaf1', cursor: 'default' },
  btnWrong: { border: '2px solid #d7191c', background: '#fdf0f0', cursor: 'default' },
  btnRevealed: { border: '1px solid #ddd', background: '#f9f9f9', cursor: 'default' },
  submitBtn: { padding: '0.6rem 2rem', border: 'none', borderRadius: '6px', background: '#2c7bb6', color: '#fff', fontSize: '0.9rem', cursor: 'pointer' },
  submitBtnDisabled: { background: '#ccc', cursor: 'default' },
  nextBtn: { padding: '0.6rem 2rem', border: 'none', borderRadius: '6px', background: '#2c7bb6', color: '#fff', fontSize: '0.9rem', cursor: 'pointer', marginTop: '0.5rem' },
  feedback: { fontSize: '0.9rem', margin: '1rem 0', padding: '0.6rem', borderRadius: '4px' },
  feedbackCorrect: { background: '#eafaf1', color: '#1e8449' },
  feedbackWrong: { background: '#fdf0f0', color: '#c0392b' },
  score: { fontSize: '3rem', fontWeight: '700', color: '#2c7bb6', margin: '1rem 0' },
  resultText: { fontSize: '0.95rem', color: '#555', lineHeight: '1.5' },
  introText: { fontSize: '0.9rem', color: '#555', lineHeight: '1.5', marginBottom: '1.5rem' },
  startBtn: { padding: '0.7rem 2.5rem', border: 'none', borderRadius: '6px', background: '#2c7bb6', color: '#fff', fontSize: '1rem', cursor: 'pointer' },
}

class ListeningTest extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      phase: 'intro',
      rounds: shuffle(ALL_QUESTIONS),
      currentRound: 0,
      shuffledOrder: [],
      selectedIdx: -1,
      submitted: false,
      results: [],
    }
    this.audio = null
    this.playingIdx = -1
  }

  componentWillUnmount() {
    this.stopAll()
  }

  stopAll() {
    if (this.audio) {
      this.audio.onended = null
      this.audio.pause()
      this.audio.src = ''
      this.audio.load()
      this.audio = null
    }
    this.playingIdx = -1
    this.forceUpdate()
  }

  playClip(idx) {
    this.stopAll()
    const id = this.state.shuffledOrder[idx]
    this.audio = new Audio(CLIPS[id])
    this.audio.play().catch(() => {})
    this.playingIdx = idx
    this.audio.onended = () => { this.playingIdx = -1; this.forceUpdate() }
    this.setState({ selectedIdx: idx })
  }

  startTest() {
    this.setState({
      phase: 'playing',
      currentRound: 0,
      shuffledOrder: shuffle(['original', 'fdk_lpf', 'fdk_nolpf']),
      selectedIdx: -1,
      submitted: false,
      results: [],
    })
  }

  submit() {
    this.stopAll()
    const { rounds, currentRound, shuffledOrder, selectedIdx, results } = this.state
    const round = rounds[currentRound]
    const chosen = shuffledOrder[selectedIdx]
    const correct = chosen === round.answer
    this.setState({ submitted: true, results: [...results, correct] })
  }

  nextRound() {
    const { currentRound, rounds } = this.state
    if (currentRound + 1 >= rounds.length) {
      this.setState({ phase: 'results' })
    } else {
      this.setState({
        currentRound: currentRound + 1,
        shuffledOrder: shuffle(['original', 'fdk_lpf', 'fdk_nolpf']),
        selectedIdx: -1,
        submitted: false,
      })
    }
  }

  renderIntro() {
    return (
      <div style={styles.card}>
        <h2 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>Can you hear the difference?</h2>
        <p style={styles.introText}>
          Three clips of the same audio: the original, and two encodes at 256 kbps
          (one with the low-pass filter, one without).
          Listen to each, then pick the one we ask for. Ten rounds.
        </p>
        <button style={styles.startBtn} onClick={() => this.startTest()}>Start</button>
      </div>
    )
  }

  renderRound() {
    const { rounds, currentRound, shuffledOrder, selectedIdx, submitted } = this.state
    const round = rounds[currentRound]
    const labels = ['A', 'B', 'C']
    const pct = (currentRound / rounds.length) * 100

    let correct = false
    if (submitted) {
      correct = shuffledOrder[selectedIdx] === round.answer
    }

    return (
      <div style={styles.card}>
        <div style={styles.progress}>Round {currentRound + 1} of {rounds.length}</div>
        <div style={styles.progressBar}><div style={styles.progressFill(pct)}></div></div>
        <p style={styles.question}>{round.question}</p>
        <div style={styles.players}>
          {shuffledOrder.map((id, i) => {
            let btnStyle = { ...styles.btn }
            let label = labels[i]

            if (submitted) {
              if (shuffledOrder[i] === round.answer) {
                btnStyle = { ...btnStyle, ...styles.btnCorrect }
                label = NAMES[id] + ' ✓'
              } else if (i === selectedIdx && !correct) {
                btnStyle = { ...btnStyle, ...styles.btnWrong }
                label = NAMES[id] + ' ✗'
              } else {
                btnStyle = { ...btnStyle, ...styles.btnRevealed }
                label = NAMES[id]
              }
            } else {
              if (i === selectedIdx) btnStyle = { ...btnStyle, ...styles.btnSelected }
            }

            return (
              <button key={i} style={btnStyle}
                onClick={submitted ? undefined : () => this.playClip(i)}>
                {label}
              </button>
            )
          })}
        </div>

        {!submitted && (
          <button
            style={selectedIdx < 0 ? { ...styles.submitBtn, ...styles.submitBtnDisabled } : styles.submitBtn}
            disabled={selectedIdx < 0}
            onClick={() => this.submit()}>
            Submit
          </button>
        )}

        {submitted && (
          <div>
            <div style={{ ...styles.feedback, ...(correct ? styles.feedbackCorrect : styles.feedbackWrong) }}>
              {correct ? 'Correct!' : 'That was ' + NAMES[shuffledOrder[selectedIdx]] + '.'}
            </div>
            <button style={styles.nextBtn} onClick={() => this.nextRound()}>
              {currentRound < rounds.length - 1 ? 'Next' : 'See results'}
            </button>
          </div>
        )}
      </div>
    )
  }

  renderResults() {
    const { results, rounds } = this.state
    const correct = results.filter(Boolean).length

    let message
    if (correct >= 8) {
      message = "Impressive. You can genuinely hear the difference. You're in a small minority."
    } else if (correct >= 5) {
      message = "You caught some. Hard to say if that's sharp ears or lucky guesses at this sample size."
    } else if (correct >= 3) {
      message = "About what you'd expect from random chance. At 256 kbps, these differences are extremely subtle."
    } else {
      message = "Most people can't reliably tell them apart at this bitrate. The filter costs almost nothing perceptually."
    }

    return (
      <div style={styles.card}>
        <h2 style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>Results</h2>
        <div style={styles.score}>{correct} / {rounds.length}</div>
        <p style={styles.resultText}>{message}</p>
      </div>
    )
  }

  render() {
    const { phase } = this.state
    if (phase === 'intro') return this.renderIntro()
    if (phase === 'playing') return this.renderRound()
    return this.renderResults()
  }
}

export default ListeningTest
